import React, { useState, useEffect, useRef } from 'react';
import './UserPlantsForm.css';
import './Buttons.css';
import './Instructions.css';
import PlantVarietyInstructions from './Utilities/PlantVarietyInstructions';
import api from '../api';

const UserPlants = ({
    currentGardener,
    plants,
    userPlantData,
    onAddUserPlant,
    onDeleteUserPlant,
    onUpdateUserPlant,
    onSelectVariety,
    selectedVariety,
    onSelectCrop,
    selectedCrop,
    isDemoMode,
    currentDate}) => {

    const defaultPlantData = {
        plantID: '',
        gardenerId: currentGardener.id,
        variety: '',
        seedDepth: '',
        seedSpacing: '',
        rowSpacing: '',
        daysToGerminate: '',
        daysToMaturity: '',
        gardenType: null,
        notes: '',
        successionInterval: '',
        dontTrack: false
    };

    const [formState, setFormState] = useState(defaultPlantData);
    const [showInstructions, setShowInstructions] = useState(false);
    const [gardenTypes, setGardenTypes] = useState([]);
    const [isRebuilding, setIsRebuilding] = useState(false);
    const [rebuildMessage, setRebuildMessage] = useState("");
    const [userPlantMessage, setUserPlantMessage] = useState(null);
    const [seasons, setSeasons] = useState([]);
    const [pendingSeasonSelections, setPendingSeasonSelections] = useState([]);

    const varietyInputRef = useRef(null);

    const isEditingVariety = !!formState.id || !!formState.userPlantID;
    const selectedCropName = plants.find(p => p.id === selectedCrop)?.name || null;

    const selectedPlant = plants.find(p => p.id === selectedCrop);
    const supportsSuccession = selectedPlant?.successionPlantings === true;

    const formatDate = (date) => {
        if (!date) return '';

        const [month, day] = date.split('-');

        const monthNames = [
            'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
            'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
        ];

        return `${ monthNames[Number(month) - 1] } ${ Number(day) } `;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        
        if (name === "plantID") {
            const normalizedID = Number(value) || null;
            setFormState({ ...formState, plantID: normalizedID });
        } else {
            setFormState({ ...formState, [name]: value });
        }
    };

    useEffect(() => {
        console.log('selected variety', selectedVariety);
        console.log('supports succession',supportsSuccession);
        console.log('selected plant', selectedPlant);
        if (selectedVariety && !selectedVariety.isNewVariety) {
            setFormState({
                ...defaultPlantData,
                ...selectedVariety, // apply selectedVariety from parent
            });
        } else if (selectedCrop && selectedVariety?.isNewVariety) {
            // New variety mode � fetch crop-level settings
            const fetchCropSettings = async () => {
                try {
                    const response = await api.get(
                        `/userPlants/crop-settings/${currentGardener.id}/${selectedCrop}`
                    );

                    const { dontTrack, successionInterval } = response.data;
                    console.log('dontTrack: ', dontTrack);
                    console.log('successionInterval: ', successionInterval);
                    setFormState({
                        ...defaultPlantData,
                        plantID: selectedCrop,
                        isNewVariety: true,

                        //default from plants table
                        seedDepth: selectedPlant?.seedDepth ?? '',
                        seedSpacing: selectedPlant?.seedSpace ?? '',
                        rowSpacing: selectedPlant?.rowSpace ?? '',
                        daysToGerminate: selectedPlant?.daysToGerminate ?? '',
                        daysToMaturity: selectedPlant?.daysToMaturity ?? '',
                        soilTemperature: selectedPlant?.germinateTemp ?? '',
                        dailyMinTemp: selectedPlant?.minTemp ?? '',
                        dailyMaxTemp: selectedPlant?.maxTemp ?? '',
                        gardenType: selectedPlant?.gardentype ?? null,

                        dontTrack: dontTrack ?? false,
                        successionInterval: successionInterval ?? ''
                    });
                } catch (err) {
                    console.error("Error fetching crop settings:", err);

                    // fallback to defaults
                    setFormState({
                        ...defaultPlantData,
                        plantID: selectedCrop,
                        isNewVariety: true
                    });
                }
            };

            fetchCropSettings();
        } else {
            // No crop or collapsed ? reset
            setFormState(defaultPlantData);
        }
    }, [selectedCrop, selectedVariety]);

    useEffect(() => {
        const formIsOpen = selectedVariety || selectedVariety?.isNewVariety;

        // Only scroll/focus when the Save New Variety button is visible
        const isNewVarietyMode = formIsOpen && !isEditingVariety;

        if (isNewVarietyMode && varietyInputRef.current) {
            varietyInputRef.current.focus();
            window.scrollTo({ top: 0, behavior: 'smooth' });        }
    }, [selectedVariety, selectedCrop, isEditingVariety]);


    useEffect(() => {
        const fetchGardenTypes = async () => {
    try {
      const response = await api.get('/gardens/all');

      const contentType = response.headers['content-type'];
      if (!contentType || !contentType.includes('application/json')) {
        throw new TypeError(`Expected JSON response, but received: ${contentType}`);
      }

      const data = response.data;
      setGardenTypes(data);
    } catch (err) {
      console.error('Error fetching garden types:', err);
    }
  };
        fetchGardenTypes();
    }, []);

    useEffect(() => {
        if (!selectedCrop) return;

        const plant = plants.find(p => p.id === selectedCrop);
        if (!plant || !plant.seasonalCrop) return;

        async function loadSeasons() {
            try {

                // get planting windows
                const response = await api.get(`/plantingRegions/${selectedCrop}`);
                const windows = response.data?.All?.windows || [];

                const userPlantID =
                    selectedVariety?.userPlantID;

                // New variety � nothing to load from xref
                if (!userPlantID) {
                    setSeasons(
                        windows.map(window => ({
                            ...window,
                            xrefCheck: 0
                        }))
                    );
                    return;
                }

                // Existing variety � load its selected windows
                const seasonResponse = await api.get(
                    `/plantingRegion/seasons?plantID=${selectedCrop}&userPlantID=${userPlantID}`
                );

                const xrefRows = seasonResponse.data || [];

                // Map xref values onto the actual planting windows
                const windowsWithXref = windows.map(window => {
                    const xref = xrefRows.find(
                        row =>
                            row.plantingRegionID ===
                            window.plantingRegionID
                    );

                    return {
                        ...window,
                        xrefCheck: xref?.xrefCheck ?? 0
                    };
                });


                setSeasons(windowsWithXref);

            } catch (err) {
                console.error("Error loading seasons:", err);
            }
        }



        loadSeasons();
    }, [selectedCrop, selectedPlant, selectedVariety]);

    const handlePlantingWindowToggle = async (plantingRegionID, checked) => {

        if (selectedVariety.isNewVariety) {

            setPendingSeasonSelections(prev =>
                checked
                    ? [...prev, plantingRegionID]
                    : prev.filter(id => id !== plantingRegionID)
            );

            return;
        }

        const userPlantID = selectedVariety.userPlantID;

        // Don't allow a seasonal variety to have zero planting windows
        if (!checked) {

            const otherSelectedWindows = seasons.filter(
                window =>
                    Number(window.xrefCheck) === 1 &&
                    window.plantingRegionID !== plantingRegionID
            );

            if (otherSelectedWindows.length === 0) {
                alert(
                    "This variety must have at least one planting window. " +
                    "Please select the new planting window before removing this one."
                );
                return;
            }
        }


        try {

            if (checked) {

                await api.post('/plantingRegion/add-season-xref', {
                    userPlantID,
                    plantingRegionID
                });

            } else {

                await api.delete(
                    `/plantingRegion/remove-season-xref?userPlantID=${userPlantID}&plantingRegionID=${plantingRegionID}`
                );

            }

            setSeasons(prev =>
                prev.map(window =>
                    window.plantingRegionID === plantingRegionID
                        ? { ...window, xrefCheck: checked ? 1 : 0 }
                        : window
                )
            );

        } catch (err) {

            console.error(
                "Error updating planting window xref:",
                err
            );

        }
    };    const handleAddUserPlant = async (e) => {
        e.preventDefault();

        const newVariety = { ...formState, plantID: selectedCrop };

        // REQUIRE at least one planting region for seasonal crops
        if (selectedPlant?.seasonalCrop && pendingSeasonSelections.length === 0) {
            alert("Please select at least one planting window before saving this variety.");
            return;
        }


        setFormState(newVariety);
        onAddUserPlant(newVariety, pendingSeasonSelections);

        setPendingSeasonSelections([]);

    };

    const handleUpdateUserPlant = (e) => {
        e.preventDefault();

        const updated = { ...formState, plantID: selectedCrop, gardenerId: currentGardener.id };

        setFormState(updated);
        onUpdateUserPlant(updated);

        showUserPlantMessage(
            "Some changes may impact your garden plan. Rebuild your plan now or changes will be reflected tomorrow."
        );
    };

    const handleDeleteUserPlant = (e) => {
        e.preventDefault();
        console.log('selectedVariety ', selectedVariety);
        const deleted = { ...formState, plantID: selectedCrop };

        setFormState(deleted);
        onDeleteUserPlant(selectedVariety);
    };

    const getVarietiesForCrop = (plantID) => {
        return Array.isArray(userPlantData)
            ? userPlantData.filter((data) => data.plantID === plantID)
            : [];
    };


const handleRebuildPlan = async () => {
    try {
        setIsRebuilding(true);
        setRebuildMessage("Rebuilding your garden plan...");

        await api.post('/buildGardenerSchedule/runTasks', {
            gardener: currentGardener,
            currentDate: currentDate
        });

        setRebuildMessage("Finished!");
        setTimeout(() => {
            setIsRebuilding(false);
            setRebuildMessage("");
        }, 800);

    } catch (err) {
        console.error("Error rebuilding plan:", err);
        setIsRebuilding(false);

        //mid instruction message
        if (err.response?.status === 409 && err.response?.data?.error === "MID_INSTRUCTION") {
            alert("It looks like you are in the middle of a task instruction and still answering questions.  Please finish that task before rebuilding your garden plan.");
            return;
        }

        alert("Something went wrong rebuilding your plan.");
    }
};

    function showUserPlantMessage(message) {
        setUserPlantMessage(message);
        setTimeout(() => {
            setUserPlantMessage(null);
        }, 4000);
    }

    return (
            <div className="plant-variety-form">
            <h2 className="form-heading">
                {currentGardener?.name || 'Gardener'} here is where your garden gets specific!
            </h2>

            <button
                className="button rebuild-button"
                onClick={handleRebuildPlan}
                disabled={isDemoMode || isRebuilding}
            >
                {isRebuilding ? "Working..." : "Rebuild My Garden Plan"}
            </button>

            {userPlantMessage && (
                <div className="gj-toast">
                    {userPlantMessage}
                </div>
            )}

            {isDemoMode && (
                <p className="demo-hint">Rebuilding your plan is disabled in demo mode.</p>
            )}

            {isRebuilding && (
                <div className="loading-overlay">
                    <div className="spinner"></div>
                    <p>{rebuildMessage}</p>
                </div>
            )}
                <div className="instructions-toggle-wrapper">
                    <button
                        className="button"
                        onClick={() => setShowInstructions(prev => !prev)}
                        aria-expanded={showInstructions}
                        aria-controls="plant-instructions"
                    >
                        {showInstructions ? 'Hide Help' : 'Show Help'}
                    </button>

                    {showInstructions && (
                        <div className="instructions-panel">
                            <PlantVarietyInstructions />
                        </div>
                    )}
            </div>
            <h2 className="form-sub-heading">
                {!selectedCrop ? (
                    `${currentGardener?.name || 'Gardener'}, select a crop or add a new one to get started.`
                ) : !selectedCropName ? (
                        `No crop selected - please select a crop from your current list of crops or add a new one.`
                ) : selectedVariety?.isNewVariety ? (
                        `${selectedCropName} selected - enter a new variety below`
                ) : selectedVariety ? (
                            `${selectedCropName} - ${selectedVariety.variety} Variety: Let's get specific`
                ) : (
                                `${selectedCropName} selected - now choose a variety or create a new one`
                )}
            </h2>

            {selectedVariety || selectedVariety?.isNewVariety ? (
                <form id="user-plants-form">

                    {/* Don't Track Toggle */}

                    <label htmlFor="dontTrack" className="dont-track-lable">
                        <strong>Don't Track {selectedCropName}</strong>
                    </label>
                            <input
                                type="checkbox"
                                id="dontTrack"
                                name="dontTrack"
                                checked={formState.dontTrack || false}
                                onChange={(e) => {
                                    const value = e.target.checked;
                                    setFormState(prev => ({ ...prev, dontTrack: value }));
                                }}
                            />

                        <p className="toggle-hint">
                            When enabled, GrowYourFoodApp will only create planting tasks for this crop.
                        </p>


                    {/* Variety Basics */}
                    <fieldset className="variety-basics">
                        <legend className="banner-section-title">Variety Basics</legend>
                        <div className="contact-method">
                            <label>Variety:</label>
                            <input type="text" name="variety" value={formState.variety || ''} onChange={handleChange} ref={varietyInputRef} />
                        </div>
                        <div className="contact-method">
                            <label htmlFor="garden">Garden Type:</label>
                            <select
                                name="gardenType"
                                id="gardenID"
                                value={formState.gardenType || ''}
                                onChange={handleChange}
                            >
                                <option value="">Select your garden type</option>
                                {gardenTypes.map((garden) => (
                                    <option key={garden.gardenID} value={garden.gardenID}>
                                        {garden.Type} - {garden.description}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </fieldset>

                    {/* Planting Details */}
                    <fieldset className="planting-details">
                        <legend className="banner-section-title">Planting Details</legend>
                        <div className="contact-method">
                            <label>Seed Depth (inches):</label>
                            <input type="number" name="seedDepth" value={formState.seedDepth || ''} onChange={handleChange} />
                        </div>
                        <div className="contact-method">
                            <label>Seed Spacing (inches):</label>
                            <input type="number" name="seedSpacing" value={formState.seedSpacing || ''} onChange={handleChange} />
                        </div>
                        <div className="contact-method">
                            <label>Row Spacing (feet):</label>
                            <input type="number" name="rowSpacing" value={formState.rowSpacing || ''} onChange={handleChange} />
                        </div>
                    </fieldset>

                    {/* Growth Timeline */}
                    <fieldset className="growth-timeline">
                        <legend className="banner-section-title">Growth Timeline</legend>
                        <div className="contact-method">
                            <label>Days to Germinate:</label>
                            <input type="text" name="daysToGerminate" value={formState.daysToGerminate || ''} onChange={handleChange} />
                        </div>
                        <div className="contact-method">
                            <label>Days to Maturity:</label>
                            <input type="text" name="daysToMaturity" value={formState.daysToMaturity || ''} onChange={handleChange} />
                        </div>
                    </fieldset>

{supportsSuccession && (
    <fieldset className="succession-settings highlight-block">
        <legend className="banner-section-title">Succession Planting</legend>

        <div className="contact-method">
            <label>Succession Interval (days):</label>
            <input
                type="number"
                name="successionInterval"
                value={formState.successionInterval || ''}
                onChange={handleChange}
                min="1"
            />
        </div>
    </fieldset>
)}
                    {/* Seasons */}
                    {selectedPlant?.seasonalCrop && seasons.length > 0 && (
                        <fieldset className="seasonal-section highlight-block">
                            <legend className="banner-section-title">Planting Windows</legend>

                            <div className="section-subtitle">
                                Choose the planting window(s) for this variety:
                            </div>

                        {seasons.map(window => (
                            <div
                                key={window.plantingRegionID}
                                className="contact-method"
                            >
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={
                                            selectedVariety?.userPlantID
                                                ? window.xrefCheck === 1
                                                : pendingSeasonSelections.includes(window.plantingRegionID)
                                        }
                                        onChange={(e) =>
                                            handlePlantingWindowToggle(
                                                window.plantingRegionID,
                                                e.target.checked
                                            )
                                        }
                                    />
                                    <span>
                                        {window.steps?.map((step, index) => (
                                            <React.Fragment key={index}>
                                                {index > 0 && " "}

                                                <strong>
                                                    {step.action === "Start indoors"
                                                        ? "SI"
                                                        : step.action === "Transplant outdoors"
                                                        ? "TP"
                                                        : step.action === "Direct sow"
                                                        ? "DS"
                                                        : step.action}
                                                    :
                                                </strong>{" "}

                                                {formatDate(step.startPlant)}
                                                {" - "}
                                                {formatDate(step.endPlant)}
                                            </React.Fragment>
                                        ))}
                                    </span>
                                </label>
                            </div>
                        ))}

                        </fieldset>
                    )}

                    {/* Notes */}
                    <fieldset className="notes-planning">
                        <legend className="banner-section-title">Notes & Planning</legend>
                        <div className="contact-method">
                            <label>Notes:</label>
                            <input type="text" name="notes" value={formState.notes || ''} onChange={handleChange} />
                        </div>
                    </fieldset>

                    {/* Actions */}
                    <div className="form-actions">
                        {isEditingVariety ? (
                            <>
                                <button 
                                    type="button" 
                                    className="button" 
                                    onClick={handleUpdateUserPlant}
                                    disabled={isDemoMode}
                                 >
                                    Update Variety
                                </button>
                                <button 
                                    type="button" 
                                    className="button" 
                                    onClick={handleDeleteUserPlant}
                                    disabled={isDemoMode}
                                >
                                    Delete Variety
                                </button>
                                      {isDemoMode && <p className="demo-hint">Editing varieties is disabled in demo mode.</p>}
                            </>
                        ) : (
                          <>
                            <button 
                                type="button" 
                                className="button" 
                                onClick={handleAddUserPlant}
                                disabled={isDemoMode || !formState.variety?.trim()}
                             >
                                Save New Variety
                            </button>
                           {isDemoMode && <p className="demo-hint">Saving new varieties is disabled in demo mode.</p>}
                           
                           {!formState.variety?.trim() && !isDemoMode && (
                                <p className="demo-hint">Please enter a variety name before saving.</p>
                           )}
                          </>
                        )}
                    </div>
                </form>
            ) : (
                <p className="form-prompt">
                    
                </p>
            )}
        </div>
    );

};

export default UserPlants;
